import { useFeedbackStore } from '@/stores/feedbackStore';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import { FeedbackRating } from '@/utils/feedback';
import { fetchCbAiApi, type AiRoutingContext } from '@/services/aero/cbAiApi';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { beehivePostApiHelper } from '@/services/beehive';
import { STORAGE_KEY_SITE_ID, STORAGE_KEY_SITE_NAME } from '@context/explorer';
import { useFetchUser } from '@/auth/hooks';
import { useExplorer } from './useExplorer';

export const useFeedback = () => {
  const {
    rating,
    isModalOpen,
    isSubmitting,
    email,
    comment,
    commentErrors,
    setRating,
    setIsModalOpen,
    setIsSubmitting,
    setEmail,
    setComment,
    setCommentErrors,
    resetFeedback,
  } = useFeedbackStore();

  const { currentSessionId } = useCodeGenerationStore();
  const { user, isLoggedIn } = useFetchUser();
  const { siteName } = useExplorer();
  const tenantName = siteName || user?.active_site?.site_name;

  const routingContext = useMemo<AiRoutingContext>(
    () => ({
      isAuthenticated: Boolean(isLoggedIn),
      tenantName,
      userToken: user?.token,
    }),
    [isLoggedIn, tenantName, user?.token]
  );

  const toastTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isModalOpen) {
      setRating(null);
    }
  }, [isModalOpen, setRating]);

  const submitFeedbackToBeehive = useCallback(
    async (
      feedbackRating: FeedbackRating,
      emailValue: string,
      commentText: string
    ) => {
      const siteId =
        typeof window !== 'undefined'
          ? localStorage.getItem(STORAGE_KEY_SITE_ID)
          : null;
      const siteName =
        typeof window !== 'undefined'
          ? localStorage.getItem(STORAGE_KEY_SITE_NAME)
          : null;

      const isHelpful = feedbackRating === FeedbackRating.POSITIVE;

      const payload = {
        url: typeof window !== 'undefined' ? window.location.href : '',
        is_helpful: isHelpful ? 1 : 0,
        comments: commentText || '',
        timestamp: new Date().toISOString(),
        feedback_source: 'API-EXPLORER',
        email: emailValue || '',
        ...(siteId && { site_id: siteId }),
        ...(siteName && { site_name: siteName }),
      };

      return await beehivePostApiHelper(
        'public/api/help-docs/feedback',
        payload,
        'feedback'
      );
    },
    []
  );

  const submitAiFeedback = useCallback(
    async (
      feedbackRating: FeedbackRating,
      emailValue: string,
      commentText?: string
    ) => {
      if (!currentSessionId) {
        throw new Error(
          'No active session found. Please start a conversation first.'
        );
      }

      setIsSubmitting(true);
      setCommentErrors([]);

      try {
        const trimmedComment = commentText?.trim() || '';

        const [backstageResult, beehiveResult] = await Promise.allSettled([
          fetchCbAiApi(
            `/api/public/api_explorer/agent_sessions/${currentSessionId}/feedback`,
            {
              method: 'POST',
              body: JSON.stringify({
                feedback_type: feedbackRating,
                comment: trimmedComment || null,
              }),
            },
            'agent_feedback_submit',
            routingContext
          ),
          submitFeedbackToBeehive(feedbackRating, emailValue, trimmedComment),
        ]);

        const backstageFailed = backstageResult.status === 'rejected';
        const beehiveFailed = beehiveResult.status === 'rejected';

        if (backstageFailed && beehiveFailed) {
          const backstageError = backstageResult.reason;
          const errorMessage =
            backstageError instanceof Error
              ? backstageError.message
              : 'Failed to submit feedback';
          throw new Error(errorMessage);
        } else if (backstageFailed) {
          console.warn(
            'Backstage feedback submission failed:',
            backstageResult.reason
          );
        } else if (beehiveFailed) {
          console.warn(
            'Beehive feedback submission failed:',
            beehiveResult.reason
          );
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to submit feedback';
        throw new Error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      currentSessionId,
      setIsSubmitting,
      setCommentErrors,
      submitFeedbackToBeehive,
      routingContext,
    ]
  );

  const submitGeneralFeedback = useCallback(
    async (
      feedbackRating: FeedbackRating,
      emailValue: string,
      commentText: string
    ) => {
      setIsSubmitting(true);
      setCommentErrors([]);

      try {
        await submitFeedbackToBeehive(feedbackRating, emailValue, commentText);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to submit feedback';
        throw new Error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [setIsSubmitting, setCommentErrors, submitFeedbackToBeehive]
  );

  const closeFeedback = useCallback(
    (clearFields = false) => {
      setRating(null);
      setIsModalOpen(false);
      setCommentErrors([]);
      if (clearFields) {
        setEmail('');
        setComment('');
      }
    },
    [setRating, setIsModalOpen, setEmail, setComment, setCommentErrors]
  );

  return {
    rating,
    isModalOpen,
    isSubmitting,
    email,
    comment,
    commentErrors,
    currentSessionId,

    setRating,
    setIsModalOpen,
    setEmail,
    setComment,
    setCommentErrors,

    submitAiFeedback,
    submitGeneralFeedback,
    closeFeedback,
    resetFeedback,
  };
};
